(() => {
  const selector = '.threedi-reveal[data-reveal]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ThreeDiRevealController {
    constructor() {
      this.observer = null;
      this.mutationObserver = null;
      this.pendingRoots = new Set();
      this.mutationFrame = null;

      this.onIntersect = this.onIntersect.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onMutations = this.onMutations.bind(this);

      this.bindEvents();
      this.initialize(document);
      this.observeDynamicContent();
    }

    bindEvents() {
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('shopify:section:load', (event) => {
        this.initialize(event.target);
        if (window.Shopify?.designMode) this.revealWithin(event.target);
      });
      document.addEventListener('shopify:section:unload', (event) => this.destroyWithin(event.target));
      document.addEventListener('shopify:section:select', (event) => this.revealWithin(event.target));
      document.addEventListener('shopify:block:select', (event) => this.revealClosest(event.target));
      document.addEventListener('shopify:section:reorder', () => this.initialize(document));
      window.addEventListener('pageshow', () => this.initialize(document));
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', this.onMotionChange);
      else reducedMotion.addListener?.(this.onMotionChange);
    }

    getObserver() {
      if (this.observer || reducedMotion.matches || !('IntersectionObserver' in window)) return this.observer;

      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.14,
      });
      return this.observer;
    }

    initialize(root) {
      const elements = this.findElements(root);
      if (!elements.length) return;

      const observer = this.getObserver();
      elements.forEach((element) => {
        if (element.dataset.revealInitialized === 'true') return;
        element.dataset.revealInitialized = 'true';
        this.applyElementSettings(element);

        if (element.dataset.reveal === 'none' || reducedMotion.matches || !observer) {
          this.reveal(element);
          return;
        }

        if (element.hasAttribute('data-reveal-immediate')) {
          element.classList.add('is-reveal-pending');
          requestAnimationFrame(() => requestAnimationFrame(() => this.reveal(element)));
          return;
        }

        try {
          observer.observe(element);
          element.classList.add('is-reveal-pending');
        } catch (error) {
          this.reveal(element);
        }
      });
    }

    applyElementSettings(element) {
      const delay = Number.parseInt(element.dataset.revealDelay, 10);
      if (Number.isFinite(delay) && delay >= 0) {
        element.style.setProperty('--reveal-delay', `${Math.min(delay, 2000)}ms`);
      }
    }

    findElements(root) {
      if (!(root instanceof Element) && root !== document) return [];
      const elements = root.matches?.(selector) ? [root] : [];
      return elements.concat(Array.from(root.querySelectorAll?.(selector) || []));
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.reveal(entry.target);
      });
    }

    reveal(element) {
      element.classList.add('is-revealed');
      this.observer?.unobserve(element);
    }

    revealWithin(root) {
      this.findElements(root).forEach((element) => this.reveal(element));
    }

    revealClosest(target) {
      const element = target?.matches?.(selector) ? target : target?.closest?.(selector);
      if (element) this.reveal(element);
      else this.revealWithin(target);
    }

    onFocusIn(event) {
      this.revealClosest(event.target);
    }

    onMotionChange(event) {
      if (!event.matches) return;
      this.observer?.disconnect();
      this.observer = null;
      this.revealWithin(document);
    }

    destroyWithin(root) {
      this.findElements(root).forEach((element) => this.observer?.unobserve(element));
    }

    observeDynamicContent() {
      if (!('MutationObserver' in window) || !document.body) return;
      this.mutationObserver = new MutationObserver(this.onMutations);
      this.mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    onMutations(mutations) {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) this.pendingRoots.add(node);
        });
      });

      if (!this.pendingRoots.size || this.mutationFrame) return;
      this.mutationFrame = requestAnimationFrame(() => {
        this.pendingRoots.forEach((root) => this.initialize(root));
        this.pendingRoots.clear();
        this.mutationFrame = null;
      });
    }

  }

  const start = () => new ThreeDiRevealController();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
